import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react-router';

import { client } from '../client';
import { FaCopy } from 'react-icons/fa';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import DashboardLayout from './layout/DashboardLayout';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui';
import './index.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isLoaded, userId } = useAuth();
  const [pages, setPages] = useState([]);
  const [copiedPageIds, setCopiedPageIds] = useState(new Set());

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (!userId) {
      navigate('/');
      return;
    }

    const createUser = async () => {
      try {
        await client.createIfNotExists({
          _type: 'user',
          _id: userId,
          userId: userId,
        });
      } catch (error) {
        console.error('Error creating user:', error);
      }
    };

    createUser();
  }, [isLoaded, navigate, userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    client.fetch(`*[_type == "page" && user._ref == "${userId}"] | order(_createdAt desc)`).then((data) => {
      setPages(data);
    });
  }, [userId]);

  const handleCopy = (id) => {
    navigator.clipboard.writeText(`https://say-yes-tn.netlify.app/page/${id}`);
    setCopiedPageIds(new Set(copiedPageIds).add(id));
  };

  const answeredPages = useMemo(() => pages.filter((page) => page.status).length, [pages]);
  const waitingPages = pages.length - answeredPages;

  return (
    <DashboardLayout
      title="Proposal Pages"
      subtitle="Track your page statuses and quickly share links."
      actions={
        <Button onClick={() => navigate('/dashboard/create')} size="sm">
          Create a page
        </Button>
      }
    >
      <section className="dashboard-stats-grid">
        <Card>
          <CardHeader>
            <CardTitle>Total Pages</CardTitle>
            <CardDescription>All proposal pages you created</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="dashboard-stat-value">{pages.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Answered</CardTitle>
            <CardDescription>Pages that received a yes/no response</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="dashboard-stat-value">{answeredPages}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Waiting</CardTitle>
            <CardDescription>Pages still awaiting response</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="dashboard-stat-value">{waitingPages}</p>
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Your Pages</CardTitle>
          <CardDescription>Use copy action to share the page instantly.</CardDescription>
        </CardHeader>
        <CardContent>
          {pages.length === 0 ? (
            <div className="empty-pages-message">
              <p>Create your first page now.</p>
            </div>
          ) : (
            <div className="dashboard-pages-table">
              <div className="dashboard-pages-table-header">
                <p>Partner Name</p>
                <p>Status</p>
                <p>Action</p>
              </div>
              {pages.map((page) => (
                <div className="dashboard-pages-row" key={page._id}>
                  <p>{page.partnerName}</p>
                  <div>
                    {page.status ? <Badge variant="secondary">Answered</Badge> : <Badge variant="outline">Waiting</Badge>}
                  </div>
                  <div>
                    {!copiedPageIds.has(page._id) ? (
                      <button className="copy-action" onClick={() => handleCopy(page._id)} type="button">
                        <FaCopy />
                        Copy link
                      </button>
                    ) : (
                      <span className="copied-action">
                        <IoIosCheckmarkCircleOutline />
                        Copied
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default Dashboard;
